import { BasicMonitorStatus, BarInterval, PipelineStatusData } from '@/services/enums';
import { create } from 'zustand';
import { stockMonitorService, StockMonitorTask } from '../services/stockMonitorService';
import { useToastStore } from './useToastStore';

interface StockMonitorState {
  // 状态
  monitors: StockMonitorTask[];
  totalMonitors: number;
  runningCount: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  isSyncingResearch: boolean;
  error: string | null;
  stoppingMonitors: Set<string>; // 正在停止的监控任务ID
  startingMonitors: Set<string>; // 正在启动的监控任务ID
  autoMonitorPipelineStatus: PipelineStatusData | null;
  pipelineEnabled: boolean;

  // Actions
  refreshMonitors: (symbol?: string, status?: BasicMonitorStatus, page?: number, pageSize?: number) => Promise<void>;
  silentRefreshMonitors: (
    symbol?: string,
    status?: BasicMonitorStatus,
    page?: number,
    pageSize?: number,
  ) => Promise<void>; // 静默刷新（不显示加载状态）
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  stopMonitor: (monitorId: string) => Promise<void>;
  startMonitor: (monitorId: string, symbol: string, bar: BarInterval, enableAutoTrading?: boolean) => Promise<void>;
  deleteMonitor: (monitorId: string) => Promise<void>;
  deleteStoppedMonitors: () => Promise<number>;
  updateMonitorStatus: (symbol: string, bar: BarInterval, status: BasicMonitorStatus, monitorId?: string) => void;
  clearError: () => void;
  getMonitorStatus: (monitorId: string) => Promise<StockMonitorTask>;
  // Auto Monitor Actions
  getPipelineStatus: () => Promise<void>;
  togglePipelineStatus: () => Promise<void>;
  getResearchStatus: () => Promise<{
    pipelineStatus?: PipelineStatusData | null;
  }>;
  getResearchConfig: () => Promise<{
    monitorCount: number;
    strategyIds: string[];
    excludedSymbols: string[];
    minAmount?: number;
    filterAnalyzed?: boolean;
    symbolKeyword?: string;
  } | null>;
  syncResearchTasks: (
    monitorCount: number,
    excludedSymbols: string[],
    strategyIds: string[],
    minAmount?: number,
    filterAnalyzed?: boolean,
    symbolKeyword?: string,
  ) => Promise<void>;
}

export const useStockMonitorStore = create<StockMonitorState>((set, get) => ({
  // 初始状态
  monitors: [],
  totalMonitors: 0,
  runningCount: 0,
  currentPage: 1,
  pageSize: 10,
  isLoading: false,
  isSyncingResearch: false,
  error: null,
  stoppingMonitors: new Set(),
  startingMonitors: new Set(),
  autoMonitorPipelineStatus: null,
  pipelineEnabled: true,

  // 刷新监控任务列表（添加防重复请求保护）
  refreshMonitors: async (symbol?: string, status?: BasicMonitorStatus, page?: number, pageSize?: number) => {
    const state = get();
    // 如果正在加载，跳过重复请求
    if (state.isLoading) {
      return;
    }

    const targetPage = page ?? state.currentPage;
    const targetPageSize = pageSize ?? state.pageSize;

    set({ isLoading: true, error: null });
    try {
      const { monitors, total, runningCount } = await stockMonitorService.getAllRunningMonitors(
        status,
        symbol,
        targetPage,
        targetPageSize,
      );
      set({
        monitors,
        totalMonitors: total,
        runningCount,
        currentPage: targetPage,
        pageSize: targetPageSize,
        isLoading: false,
      });
    } catch (error: any) {
      const errorMessage = error.message || '获取股票监控任务列表失败';
      set({ error: errorMessage, isLoading: false });
      useToastStore.getState().show(errorMessage, 'error');
    }
  },

  // 获取管道状态
  getPipelineStatus: async () => {
    try {
      const enabled = await stockMonitorService.getPipelineStatus();
      set({ pipelineEnabled: enabled });
    } catch (error: any) {
      console.error('获取管道状态失败:', error);
    }
  },

  // 切换管道状态
  togglePipelineStatus: async () => {
    const { pipelineEnabled } = get();
    const targetState = !pipelineEnabled;

    try {
      const result = await stockMonitorService.togglePipelineStatus(targetState);
      set({ pipelineEnabled: result });
      useToastStore.getState().show(`研报管道已${result ? '开启' : '停止'}`, result ? 'success' : 'info');
    } catch (error: any) {
      useToastStore.getState().show('切换管道状态失败: ' + error.message, 'error');
    }
  },

  // 静默刷新监控任务列表（不显示加载状态，不显示错误提示）
  silentRefreshMonitors: async (symbol?: string, status?: BasicMonitorStatus, page?: number, pageSize?: number) => {
    const state = get();
    // 如果正在加载，跳过重复请求
    if (state.isLoading) {
      return;
    }

    const targetPage = page ?? state.currentPage;
    const targetPageSize = pageSize ?? state.pageSize;

    try {
      const { monitors, total, runningCount } = await stockMonitorService.getAllRunningMonitors(
        status,
        symbol,
        targetPage,
        targetPageSize,
      );
      set({ monitors, totalMonitors: total, runningCount, currentPage: targetPage, pageSize: targetPageSize }); // 只更新数据，不设置 isLoading
    } catch (error: any) {
      // 静默刷新失败时不显示错误提示，避免干扰用户
      // 只记录错误，不更新状态
    }
  },

  setPage: (page: number) => set({ currentPage: page }),
  setPageSize: (pageSize: number) => set({ pageSize }),

  // 停止监控任务
  stopMonitor: async (monitorId: string) => {
    const { stoppingMonitors } = get();
    if (stoppingMonitors.has(monitorId)) {
      return; // 已经在停止中
    }

    set({
      stoppingMonitors: new Set([...Array.from(stoppingMonitors), monitorId]),
    });

    try {
      await stockMonitorService.stopMonitor(monitorId);

      // 更新监控任务状态为 stopped，而不是移除
      const { monitors } = get();
      set({
        monitors: monitors.map((m) => (m.monitorId === monitorId ? { ...m, status: 'stopped' as const } : m)),
        stoppingMonitors: new Set(Array.from(stoppingMonitors).filter((id) => id !== monitorId)),
      });

      useToastStore.getState().show('股票监控任务已停止', 'success');
    } catch (error: any) {
      const errorMessage = error.message || '停止股票监控任务失败';
      set({
        stoppingMonitors: new Set(Array.from(stoppingMonitors).filter((id) => id !== monitorId)),
      });
      useToastStore.getState().show(errorMessage, 'error');
    }
  },

  // 启动监控任务
  startMonitor: async (monitorId: string, symbol: string, bar: BarInterval, enableAutoTrading: boolean = false) => {
    const { startingMonitors } = get();
    if (startingMonitors.has(monitorId)) {
      return; // 已经在启动中
    }

    set({
      startingMonitors: new Set([...Array.from(startingMonitors), monitorId]),
    });

    try {
      // 股票监控暂时不涉及 systemPromptId 的传递，如果需要后续可以添加
      await stockMonitorService.startMonitor(symbol, bar, enableAutoTrading);

      // 更新监控任务状态为 running
      const { monitors } = get();
      set({
        monitors: monitors.map((m) => (m.monitorId === monitorId ? { ...m, status: 'running' as const } : m)),
        startingMonitors: new Set(Array.from(startingMonitors).filter((id) => id !== monitorId)),
      });

      useToastStore.getState().show('股票监控任务已启动', 'success');
    } catch (error: any) {
      const errorMessage = error.message || '启动股票监控任务失败';
      set({
        startingMonitors: new Set(Array.from(startingMonitors).filter((id) => id !== monitorId)),
      });
      useToastStore.getState().show(errorMessage, 'error');
    }
  },

  // 删除监控任务
  deleteMonitor: async (monitorId: string) => {
    try {
      await stockMonitorService.deleteMonitor(monitorId);

      // 从列表中移除该监控任务
      const { monitors } = get();
      set({
        monitors: monitors.filter((m) => m.monitorId !== monitorId),
      });

      useToastStore.getState().show('股票监控任务已删除', 'success');
    } catch (error: any) {
      const errorMessage = error.message || '删除股票监控任务失败';
      useToastStore.getState().show(errorMessage, 'error');
      throw error;
    }
  },

  // 批量删除停止运行的监控任务
  deleteStoppedMonitors: async () => {
    try {
      const deletedCount = await stockMonitorService.deleteStoppedMonitors();

      // 刷新监控列表
      const state = get();
      await state.refreshMonitors();

      useToastStore.getState().show(`成功删除 ${deletedCount} 个停止运行的股票监控任务`, 'success');
      return deletedCount;
    } catch (error: any) {
      const errorMessage = error.message || '批量删除股票监控任务失败';
      useToastStore.getState().show(errorMessage, 'error');
      throw error;
    }
  },

  // 更新单个监控任务的状态（不刷新整个列表）
  updateMonitorStatus: (symbol: string, bar: BarInterval, status: BasicMonitorStatus, monitorId?: string) => {
    const { monitors } = get();
    set({
      monitors: monitors.map((m) => {
        // 通过 symbol 和 bar 匹配监控任务
        if (m.symbol === symbol && m.bar === bar) {
          return {
            ...m,
            status,
            ...(monitorId && { monitorId }), // 如果提供了 monitorId，也更新它
          };
        }
        return m;
      }),
    });
  },

  // 清除错误
  clearError: () => {
    set({ error: null });
  },

  // 获取单个监控任务状态
  getMonitorStatus: async (monitorId: string) => {
    try {
      const monitor = await stockMonitorService.getMonitorStatus(monitorId);

      // 可选：更新本地状态中的对应监控任务
      const { monitors } = get();
      const existingMonitorIndex = monitors.findIndex((m) => m.monitorId === monitorId);

      if (existingMonitorIndex !== -1) {
        // 如果存在，更新它
        const newMonitors = [...monitors];
        newMonitors[existingMonitorIndex] = monitor;
        set({ monitors: newMonitors });
      }

      return monitor;
    } catch (error: any) {
      console.error('获取监控任务状态失败:', error);
      throw error;
    }
  },

  // 研报管理 Actions
  getResearchStatus: async () => {
    try {
      const data = await stockMonitorService.getResearchStatus();
      set({ autoMonitorPipelineStatus: data.pipelineStatus || null });
      return data;
    } catch (error: any) {
      console.error('获取研报状态失败:', error);
      throw error;
    }
  },

  // 获取研报配置
  getResearchConfig: async () => {
    try {
      const config = await stockMonitorService.getResearchConfig();
      return config;
    } catch (error: any) {
      console.error('获取研报配置失败:', error);
      throw error;
    }
  },

  // 同步研报任务
  syncResearchTasks: async (
    monitorCount: number,
    excludedSymbols: string[],
    strategyIds: string[],
    minAmount?: number,
    filterAnalyzed?: boolean,
    symbolKeyword?: string,
  ) => {
    if (get().isSyncingResearch) return;

    set({ isSyncingResearch: true });
    try {
      await stockMonitorService.syncResearchTasks(
        monitorCount,
        excludedSymbols,
        strategyIds,
        minAmount,
        filterAnalyzed,
        symbolKeyword,
      );
      useToastStore.getState().show('A股研报任务同步已启动', 'success');
    } catch (error: any) {
      const errorMessage = error.message || '启动A股研报任务同步失败';
      useToastStore.getState().show(errorMessage, 'error');
      throw error;
    } finally {
      set({ isSyncingResearch: false });
    }
  },
}));
